import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import * as Mui from "@mui/material";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DomainDisabledIcon from "@mui/icons-material/DomainDisabled";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { showToast } from "services/utils/Status";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  statusChip,
  designColors,
  designRadii,
  designShell,
  APP_BAR_OFFSET,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
} from "services/constants/designTokens";
import { companyUi } from "../adminListScreenUi";
import {
  GlobalDetailSectionCard as AdminDetailSectionCard,
  GlobalFieldLabel as AdminFieldLabel,
  GlobalNoticeBanner as AdminNoticeBanner,
  globalTextFieldSx as adminTextFieldSx,
  PAIRED_LABEL_MIN_HEIGHT,
  useExpandedContentLeft,
  GlobalBreadcrumb,
} from "components/DesignSystem/sharedUiPatterns";
import {
  detailAccentPalette,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

// Same accent palette TeamZone's Approve panels (Leave, Expense, etc.) draw
// from — cycled per field so this panel's icon rows read the same way
// instead of inventing its own colors.
const detailAccent = {
  companyName: detailAccentPalette.indigo,
  companyCode: detailAccentPalette.pink,
  gstNumber: detailAccentPalette.blue,
  email: detailAccentPalette.green,
  phone: detailAccentPalette.orange,
  address: detailAccentPalette.indigo,
};

const HeaderMetaPill = ({ children, icon = null }) => (
  <Mui.Stack
    direction="row"
    spacing={0.75}
    alignItems="center"
    sx={{
      px: 1.1,
      py: 0.55,
      borderRadius: designRadii.pill,
      border: `1px solid ${designColors.slate200}`,
      backgroundColor: designColors.slateBg,
      minWidth: 0,
    }}
  >
    {icon}
    <Mui.Typography
      sx={{
        fontSize: 12.5,
        fontWeight: 500,
        color: designColors.slate700,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Mui.Typography>
  </Mui.Stack>
);

const ScopeNotice = () => (
  <AdminNoticeBanner sx={{ mb: 2.5 }} icon={<BusinessIcon fontSize="small" />}>
    Update this company's profile, access details, and branding.
  </AdminNoticeBanner>
);

// `minHeight` (see PAIRED_LABEL_MIN_HEIGHT) is opt-in, passed by the
// expanded grid's paired fields so both inputs in a row start at the same Y
// position — forwarded straight through to GlobalFieldLabel.
const FieldLabel = ({ children, tooltip, minHeight }) => (
  <AdminFieldLabel helperText={tooltip} minHeight={minHeight}>
    {children}
  </AdminFieldLabel>
);

// Matches CreateUser/AddCompany's radio/checkbox styling — the app's teal
// brand color on check instead of MUI's default blue.
const brandCheckboxSx = {
  color: "#9CA3AF",
  "&.Mui-checked": {
    color: designColors.tealDark,
  },
  "&:hover": {
    backgroundColor: "rgba(15, 118, 110, 0.08)",
  },
};

const LOGO_MAX_BYTES = 2 * 1024 * 1024;

const formatLogoSize = (bytes = 0) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const CompanyDetailPanel = ({
  open,
  onClose,
  company,
  onMoreActions,
  onDeactivate,
  onEdit,
  onSaveEdit,
  onPermanentDelete,
  checkAccess,
  // Companies has no real approval workflow, but the permission flag still
  // exists on the submodule -- see Companies/index.js for the full
  // edit-vs-approve split this gates (Primary/Inactivate/Activate/Delete
  // here, vs. the Edit action on checkAccess).
  hasApproverAccess,
  startInEditMode = false,
  expanded = false,
  onToggleExpand,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [yesCheck, setYesCheck] = useState(false);
  const [noCheck, setNoCheck] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoMeta, setLogoMeta] = useState(null);
  const [logoMarkedForRemoval, setLogoMarkedForRemoval] = useState(false);
  const [removeLogoDialogOpen, setRemoveLogoDialogOpen] = useState(false);
  const contentLeft = useExpandedContentLeft(expanded);
  const { domain, token } = useSelector((state) => state.tokenReducer);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      companyname: company?.companyname || "",
      companycode: company?.company_code || "",
      gstno: company?.gstnumber || "",
      email: company?.email || "",
      phoneno: company?.phonenumber || "",
      address: company?.address || "",
    },
  });

  useEffect(() => {
    if (!company) return;
    reset({
      companyname: company?.companyname || "",
      companycode: company?.company_code || "",
      gstno: company?.gstnumber || "",
      email: company?.email || "",
      phoneno: company?.phonenumber || "",
      address: company?.address || "",
    });
    if (company?.gstnumber) {
      setYesCheck(true);
      setNoCheck(false);
    } else {
      setYesCheck(false);
      setNoCheck(true);
    }
    setLogoFile(null);
    setLogoPreview(company?.company_logo_url || "");
    setLogoMeta(null);
    setLogoMarkedForRemoval(false);
    setRemoveLogoDialogOpen(false);
    setEditMode(startInEditMode);
  }, [company, reset, startInEditMode]);

  useEffect(() => {
    if (!expanded) return;
    if (typeof document === "undefined") return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [expanded]);

  if (!company) return null;

  const isActive = Boolean(company?.isactive ?? company?.status === "Active");

  const setYesCheckUpdated = () => {
    if (yesCheck) {
      setNoCheck(true);
      setYesCheck(false);
      setValue("gstno", "");
    } else {
      setYesCheck(true);
      setNoCheck(false);
    }
  };

  const setNoCheckUpdated = () => {
    if (noCheck) {
      setNoCheck(false);
      setYesCheck(true);
    } else {
      setNoCheck(true);
      setYesCheck(false);
      setValue("gstno", "");
    }
  };

  const handleKeyPress = (event) => {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  };

  const handleKeyDown = (event) => {
    const allowedKeys = /^[A-Za-z\s0-9]$/;
    if (!allowedKeys.test(event.key)) {
      event.preventDefault();
    }
  };

  const checkSpecialChar = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const validateGST = (gstNo) => {
    const regex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    return regex.test(gstNo);
  };

  const checkExistingCompanyName = async (companyName) => {
    try {
      const response = await fetch(
        `${domain}check_company_name/?company_name=${companyName}`,
        {
          method: "GET",
          headers: {
            "content-type": "application/json",
            Authorization: `token ${token}`,
          },
        },
      );
      const res = await response.json();
      if (response.ok) {
        return res?.exists
          ? "That company name is already taken.Please choose a unique name."
          : true;
      }
      return true;
    } catch (error) {
      console.error("[CompanyDetailPanel] operation failed:", error);
      return true;
    }
  };

  const checkExistingCompanyCode = async (companyCode) => {
    try {
      const response = await fetch(
        `${domain}check_company_name/?company_code=${companyCode}`,
        {
          method: "GET",
          headers: {
            "content-type": "application/json",
            Authorization: `token ${token}`,
          },
        },
      );
      const res = await response.json();
      if (response.ok) {
        return res?.exists
          ? "That company code is already taken.Please choose a unique code."
          : true;
      }
      return true;
    } catch (error) {
      console.error("[CompanyDetailPanel] operation failed:", error);
      return true;
    }
  };

  const setSelectedLogo = (file, width, height) => {
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setLogoMeta({
      filename: file.name,
      width,
      height,
      fileType: file.type === "image/svg+xml" ? "SVG" : "PNG",
      fileSize: formatLogoSize(file.size),
    });
    setLogoMarkedForRemoval(false);
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    const isPng = file.type === "image/png" || file.name.toLowerCase().endsWith(".png");
    const isSvg = file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");

    if (!isPng && !isSvg) {
      showToast("error", "Only PNG or SVG logos are supported.");
      return;
    }

    if (file.size > LOGO_MAX_BYTES) {
      showToast("error", "Logo file size must be 2 MB or less.");
      return;
    }

    if (isSvg) {
      const reader = new FileReader();
      reader.onload = () => {
        const text = String(reader.result || "");
        if (/<script|javascript:|<!doctype|<!entity/i.test(text)) {
          showToast("error", "SVG logo contains unsupported content.");
          return;
        }
        const doc = new DOMParser().parseFromString(text, "image/svg+xml");
        const svg = doc.querySelector("svg");
        if (!svg || doc.querySelector("parsererror")) {
          showToast("error", "SVG logo is not readable.");
          return;
        }
        const parseSize = (value) => Number(String(value || "").match(/[\d.]+/)?.[0]);
        let width = parseSize(svg?.getAttribute("width"));
        let height = parseSize(svg?.getAttribute("height"));
        if ((!width || !height) && svg?.getAttribute("viewBox")) {
          const parts = svg.getAttribute("viewBox").split(/\s+/).map(Number);
          width = parts[2];
          height = parts[3];
        }
        setSelectedLogo(file, width, height);
      };
      reader.readAsText(file);
      return;
    }

    const img = new Image();
    const previewUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(previewUrl);
      setSelectedLogo(file, img.width, img.height);
    };
    img.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      showToast("error", "Logo image is not readable.");
    };
    img.src = previewUrl;
  };

  const handleCancelEdit = () => {
    reset({
      companyname: company?.companyname || "",
      companycode: company?.company_code || "",
      gstno: company?.gstnumber || "",
      email: company?.email || "",
      phoneno: company?.phonenumber || "",
      address: company?.address || "",
    });
    if (company?.gstnumber) {
      setYesCheck(true);
      setNoCheck(false);
    } else {
      setYesCheck(false);
      setNoCheck(true);
    }
    setLogoFile(null);
    setLogoPreview(company?.company_logo_url || "");
    setLogoMeta(null);
    setLogoMarkedForRemoval(false);
    setRemoveLogoDialogOpen(false);
    setEditMode(false);
  };

  const handleCloseAction = () => {
    if (editMode) {
      handleCancelEdit();
      return;
    }
    onClose?.();
  };

  const submitEdit = async (data) => {
    if (!onSaveEdit) return;
    setSaving(true);
    const ok = await onSaveEdit(company, data, logoFile, logoMarkedForRemoval);
    setSaving(false);
    if (ok) {
      setLogoFile(null);
      setLogoMeta(null);
      setLogoMarkedForRemoval(false);
      setEditMode(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!onPermanentDelete) return;
    setDeleting(true);
    const ok = await onPermanentDelete(company);
    setDeleting(false);
    if (ok) {
      setEditMode(false);
    }
  };

  const companyNameField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip="Update the legal or display name of the company as required."
      >
        Company Name
      </FieldLabel>
      <Mui.TextField
        {...register("companyname", {
          required: "Company name is required",
          maxLength: {
            value: 32,
            message: "Maximum length is 32 characters",
          },
          minLength: {
            value: 3,
            message: "Minimum length is 3 characters",
          },
          validate: {
            companyName: async (fieldValue) => {
              if (!/[A-Za-z]/.test(fieldValue)) {
                return "Company name must contain at least one alphabet character.";
              }
              if (/^\d+$/.test(fieldValue)) {
                return "Company name should not contain digits.";
              }
              if (fieldValue !== "" && fieldValue !== company?.companyname) {
                return await checkExistingCompanyName(fieldValue.trim());
              }
              return true;
            },
          },
        })}
        error={Boolean(errors.companyname)}
        helperText={errors.companyname?.message}
        sx={adminTextFieldSx}
        onKeyPress={handleKeyDown}
        placeholder="Enter company name"
        size="small"
        fullWidth
      />
    </Mui.Box>
  );

  const companyCodeField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip="Modify the unique identifier for the company if needed. (Note: Ensure this remains unique to avoid conflicts.)"
      >
        Company Code
      </FieldLabel>
      <Mui.TextField
        {...register("companycode", {
          required: "Company code is required",
          minLength: {
            value: 3,
            message: "At least 3 characters required",
          },
          validate: {
            companyCode: async (fieldValue) => {
              const cleanedValue = fieldValue.replace(/[a-zA-Z^-]/g, "");
              setValue("companycode", cleanedValue);
              if (
                cleanedValue !== "" &&
                cleanedValue !== company?.company_code
              ) {
                return await checkExistingCompanyCode(cleanedValue.trim());
              }
              return true;
            },
          },
        })}
        error={Boolean(errors.companycode)}
        helperText={errors.companycode?.message}
        sx={adminTextFieldSx}
        placeholder="Enter company code"
        onKeyPress={checkSpecialChar}
        InputProps={{ inputProps: { maxLength: 3 } }}
        fullWidth
        size="small"
        onBlur={async () => {
          await trigger("companycode");
        }}
      />
    </Mui.Box>
  );

  const gstField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip="Toggle between Yes or No to update the GST registration status of the company. Changing this may affect tax-related processes."
      >
        Is GST Registered
      </FieldLabel>
      <Mui.Stack direction="row" spacing={1} alignItems="center">
        <Mui.Checkbox
          checked={yesCheck}
          onChange={() => setYesCheckUpdated()}
          sx={brandCheckboxSx}
        />
        <Mui.Typography sx={{ fontSize: 16, fontWeight: 600 }}>
          Yes
        </Mui.Typography>
        <Mui.Checkbox
          checked={noCheck}
          onChange={() => setNoCheckUpdated()}
          sx={brandCheckboxSx}
        />
        <Mui.Typography sx={{ fontSize: 16, fontWeight: 600 }}>
          No
        </Mui.Typography>
      </Mui.Stack>
      {yesCheck ? (
        <Mui.TextField
          {...register("gstno", {
            required: yesCheck ? "Gstno is required" : false,
            validate: {
              gstNoValidation: (fieldValue) => {
                if (fieldValue !== "" && !noCheck) {
                  return validateGST(fieldValue)
                    ? true
                    : "GSTNO in format 22AAAAA0000A1Z5";
                }
                return true;
              },
            },
          })}
          error={Boolean(errors.gstno)}
          helperText={errors.gstno?.message}
          sx={adminTextFieldSx}
          placeholder="GSTNO in format 22AAAAA0000A1Z5"
          onKeyPress={handleKeyDown}
          disabled={!yesCheck}
          InputProps={{ inputProps: { maxLength: 15 } }}
          size="small"
          fullWidth
        />
      ) : null}
    </Mui.Box>
  );

  const emailField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip="Update the official company email address used for notifications and reports."
      >
        Email
      </FieldLabel>
      <Mui.TextField
        {...register("email", {
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid e-mail address",
          },
        })}
        error={Boolean(errors.email)}
        helperText={errors.email?.message || ""}
        placeholder="Enter email"
        size="small"
        fullWidth
        sx={adminTextFieldSx}
      />
    </Mui.Box>
  );

  const phoneField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip="Edit the company's contact number for support, alerts, or internal communication."
      >
        Phone Number
      </FieldLabel>
      <Mui.TextField
        {...register("phoneno", {
          required: "Phone number is required",
          minLength: {
            value: 10,
            message: "Minimum length is 10 digits",
          },
          maxLength: {
            value: 15,
            message: "Maximum length is 15 digits",
          },
          validate: {
            formatCheck: (value) => {
              const cleanedValue = value.replace(/[^\d-]/g, "");
              setValue("phoneno", cleanedValue);
              return true;
            },
          },
        })}
        error={Boolean(errors.phoneno)}
        helperText={errors.phoneno?.message}
        sx={adminTextFieldSx}
        onBlur={async () => {
          await trigger("phoneno");
        }}
        placeholder="Enter phone number"
        onKeyPress={handleKeyPress}
        fullWidth
        size="small"
        InputProps={{ inputProps: { maxLength: 15 } }}
      />
    </Mui.Box>
  );

  const addressField = (
    <Mui.Box>
      <FieldLabel
        minHeight={expanded ? PAIRED_LABEL_MIN_HEIGHT : undefined}
        tooltip="Modify the company's main office or billing address as necessary."
      >
        Address
      </FieldLabel>
      <Mui.TextField
        multiline
        rows={2}
        maxRows={5}
        {...register("address", { required: "Address is required" })}
        error={Boolean(errors.address)}
        helperText={errors.address?.message}
        sx={adminTextFieldSx}
        placeholder="Enter address"
        size="small"
        fullWidth
      />
    </Mui.Box>
  );

  const logoField = (
    <Mui.Box>
      <FieldLabel tooltip="You can change or remove the existing logo. Upload a new one if needed.">
        Company Logo
      </FieldLabel>
      <Mui.Box
        sx={{
          border: `1px solid ${designColors.slate200}`,
          borderRadius: designRadii.card,
          p: 1.5,
          backgroundColor: designColors.white,
        }}
      >
        {logoPreview && !logoMarkedForRemoval ? (
          <Mui.Stack spacing={1.25}>
            <Mui.Box
              sx={{
                height: 70,
                border: `1px solid ${designColors.slate200}`,
                borderRadius: designRadii.control,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: designColors.slateBg,
              }}
            >
              <img
                src={logoPreview}
                alt="Company Logo"
                style={{
                  maxWidth: "220px",
                  maxHeight: "55px",
                  objectFit: "contain",
                }}
              />
            </Mui.Box>
            <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate700 }}>
              {logoMeta
                ? `${logoMeta.filename} - ${
                    logoMeta.width && logoMeta.height
                      ? `${logoMeta.width} x ${logoMeta.height} px`
                      : "Dimensions unavailable"
                  } - ${logoMeta.fileType} - ${logoMeta.fileSize}`
                : "Current logo"}
            </Mui.Typography>
            {logoMeta ? (
              <Mui.Alert severity="success" sx={{ py: 0.5 }}>
                Ready to upload
              </Mui.Alert>
            ) : null}
            <Mui.Stack direction="row" spacing={1}>
              <Mui.Button
                variant="outlined"
                component="label"
                size="small"
                fullWidth
                disabled={saving}
                sx={{ textTransform: "none", borderRadius: designRadii.control }}
              >
                Replace Logo
                <input
                  type="file"
                  accept="image/png,image/svg+xml"
                  hidden
                  onChange={handleLogoChange}
                  disabled={saving}
                />
              </Mui.Button>
              <Mui.Button
                variant="outlined"
                color="error"
                size="small"
                fullWidth
                disabled={saving}
                onClick={() => setRemoveLogoDialogOpen(true)}
                sx={{ textTransform: "none", borderRadius: designRadii.control }}
              >
                Remove Logo
              </Mui.Button>
            </Mui.Stack>
          </Mui.Stack>
        ) : (
          <Mui.Button
            variant="outlined"
            component="label"
            sx={{
              minHeight: 116,
              alignItems: "center",
              textTransform: "none",
              border: `1px dashed ${designColors.slate300}`,
              borderRadius: designRadii.control,
              color: designColors.slate700,
              backgroundColor: designColors.white,
              "&:hover": {
                borderColor: designColors.slate400,
                backgroundColor: designColors.slateBg,
              },
            }}
            size="small"
            fullWidth
            disabled={saving}
          >
            <Mui.Stack alignItems="center" spacing={0.5}>
              <AddBoxOutlinedIcon sx={{ fontSize: 28, color: designColors.slate700 }} />
              <Mui.Typography fontSize={15} fontWeight={700}>
                Add Company Logo
              </Mui.Typography>
              <Mui.Typography fontSize={12} color={designColors.slate600}>
                Upload a logo to brand your application.
              </Mui.Typography>
            </Mui.Stack>
            <input
              type="file"
              accept="image/png,image/svg+xml"
              hidden
              onChange={handleLogoChange}
              disabled={saving}
            />
          </Mui.Button>
        )}
      </Mui.Box>
      <Mui.Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1,
          textAlign: "center",
          color: designColors.slate600,
        }}
      >
        Recommended: approximately 400 x 200 px. Logo proportions will be preserved.
        PNG preferred, SVG supported, max 2 MB.
      </Mui.Typography>
    </Mui.Box>
  );

  // Hidden entirely without approver access rather than shown with a
  // disabled or toast-blocked button -- grouped with Primary/Inactivate/
  // Activate under approve access (deleting is at least as consequential),
  // and a red "Delete Company" warning card is actively alarming to show
  // someone who was never going to be allowed to use it.
  const deleteInactiveCompanyField = !hasApproverAccess ? null : (
    <Mui.Box
      sx={{
        border: "1px solid #FCA5A5",
        borderRadius: designRadii.card,
        backgroundColor: "#FEF2F2",
        padding: 2,
      }}
    >
      <Mui.Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          color: designColors.slate900,
          paddingBottom: 1,
        }}
      >
        Delete Company
      </Mui.Typography>
      <Mui.Typography
        sx={{ fontSize: 14, color: designColors.slate700, paddingBottom: 1.5 }}
      >
        Deleting the company will permanently remove all data and revoke
        access for all associated users.
      </Mui.Typography>
      <Mui.Stack direction="row" justifyContent="flex-end">
        <Mui.Button
          onClick={handlePermanentDelete}
          disabled={deleting}
          startIcon={<DeleteIcon fontSize="small" />}
          sx={{
            minHeight: 42,
            px: 2,
            borderRadius: designRadii.control,
            textTransform: "none",
            fontWeight: 600,
            color: "#B42318",
            border: "1px solid #FCA5A5",
            backgroundColor: designColors.white,
            "&:hover": { backgroundColor: "#FEF2F2" },
          }}
        >
          Delete Company
        </Mui.Button>
      </Mui.Stack>
    </Mui.Box>
  );

  // Expanded mode has the width to spare, so short related fields pair up
  // into a 2-column grid instead of each taking its own full-width row —
  // matches AddCompany's expanded layout. The narrow collapsed drawer keeps
  // a single stacked column, same as before.
  const expandedFieldsLayout = (
    <Mui.Grid container spacing={3}>
      <Mui.Grid item xs={12} sm={6}>
        {companyNameField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {companyCodeField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {gstField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {emailField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {phoneField}
      </Mui.Grid>
      <Mui.Grid item xs={12} sm={6}>
        {addressField}
      </Mui.Grid>
      <Mui.Grid item xs={12}>
        {isActive ? logoField : deleteInactiveCompanyField}
      </Mui.Grid>
    </Mui.Grid>
  );

  const collapsedFieldsLayout = (
    <Mui.Stack spacing={2.5}>
      {companyNameField}
      {companyCodeField}
      {gstField}
      {emailField}
      {phoneField}
      {addressField}
      {isActive ? logoField : deleteInactiveCompanyField}
    </Mui.Stack>
  );

  const editFormContent = (
    <Mui.Box component="form" onSubmit={handleSubmit(submitEdit)}>
      <ScopeNotice />
      {expanded ? expandedFieldsLayout : collapsedFieldsLayout}
    </Mui.Box>
  );

  const viewContent = (
    <Mui.Stack spacing={2}>
      <AdminDetailSectionCard title="Company Details">
            <Mui.Stack>
              <RequestDetailField
                icon={BusinessIcon}
                label="Company Name"
                value={company?.companyname}
                accent={detailAccent.companyName}
              />
              <RequestDetailField
                icon={BadgeOutlinedIcon}
                label="Company Code"
                value={company?.company_code}
                accent={detailAccent.companyCode}
              />
              <RequestDetailField
                icon={ReceiptLongOutlinedIcon}
                label="GST Number"
                value={company?.gstnumber}
                accent={detailAccent.gstNumber}
              />
              <RequestDetailField
                icon={EmailOutlinedIcon}
                label="Email"
                value={company?.email}
                accent={detailAccent.email}
              />
              <RequestDetailField
                icon={CallOutlinedIcon}
                label="Phone Number"
                value={company?.phonenumber}
                accent={detailAccent.phone}
                isLast
              />
            </Mui.Stack>
          </AdminDetailSectionCard>

          <AdminDetailSectionCard title="Address">
            <Mui.Stack>
              <RequestDetailField
                icon={PlaceOutlinedIcon}
                label="Address"
                value={company?.address}
                accent={detailAccent.address}
                multiline
                isLast
              />
            </Mui.Stack>
      </AdminDetailSectionCard>
    </Mui.Stack>
  );

  // Aligned to the end (flex-end) rather than stretched full-width — matches
  // AddCompany's formActions, which this edit form mirrors (Cancel + a
  // primary Update button, right-aligned instead of the old 2-column grid
  // that force-stretched both buttons across the footer).
  // A company with isprimary:true never shows Activate/Inactivate here,
  // matching the row action menu's own rule for that case. Approve-gated
  // (not editor) to match the More Actions menu's Inactivate/Activate item
  // -- see Companies/index.js for the full edit-vs-approve split.
  const canToggleActive = hasApproverAccess && !company?.isprimary;
  const showMoreActions = hasApproverAccess && !company?.isprimary;
  const hasAnyFooterAccess = showMoreActions || canToggleActive;
  const footerContent = editMode ? (
    <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
      <SecondaryButton onClick={handleCancelEdit}>Cancel</SecondaryButton>
      {/* Unreachable via the UI once the header Edit button above is
          hidden for a non-editor, but guarded directly too rather than
          relying only on that. */}
      {isActive && checkAccess ? (
        <PrimaryButton
          onClick={handleSubmit(submitEdit)}
          loading={saving}
          disabled={saving}
        >
          Update
        </PrimaryButton>
      ) : null}
    </Mui.Stack>
  ) : !hasAnyFooterAccess ? (
    null
  ) : (
    <Mui.Stack spacing={1.5}>
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 1.5,
        }}
      >
        {showMoreActions ? (
          <Mui.Button
            variant="outlined"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => onMoreActions?.(company, e)}
            sx={{
              flex: 1,
              ...designShell.panelNeutralButton,
              "&:hover": {
                ...designShell.panelNeutralButton["&:hover"],
              },
            }}
          >
            More Actions
          </Mui.Button>
        ) : null}
        {canToggleActive ? (
          <Mui.Button
            variant="outlined"
            startIcon={isActive ? <DomainDisabledIcon /> : <AddBusinessIcon />}
            onClick={() => onDeactivate?.(company)}
            sx={{
              flex: 1,
              ...designShell.panelDangerButton,
              color: isActive ? "#B42318" : designColors.tealDark,
              borderColor: isActive ? "#F5C2C7" : "#B7E4DB",
              backgroundColor: isActive ? "#FEF3F2" : designColors.tealTint,
              "&:hover": {
                borderColor: isActive ? "#E59CA5" : designColors.tealDark,
                backgroundColor: isActive ? "#FEE4E2" : "#DDF4EE",
              },
            }}
          >
            {isActive ? "Deactivate Company" : "Activate Company"}
          </Mui.Button>
        ) : null}
      </Mui.Box>
    </Mui.Stack>
  );

  const panelContent = (
    <Mui.Box
      sx={{
        width: expanded ? "100%" : PANEL_DRAWER_WIDTH,
        maxWidth: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxSizing: "border-box",
        backgroundColor: designColors.white,
        borderRadius: expanded ? designRadii.card : 0,
      }}
      role="presentation"
    >
      <Mui.Box sx={{ p: 3, pb: 0 }}>
        {expanded ? (
          <GlobalBreadcrumb
            trail={[
              "Administration",
              { label: "Companies", onClick: handleCloseAction },
              company?.companyname || "Company Details",
            ]}
          />
        ) : null}
        <Mui.Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            pb: 2.25,
            borderBottom: `1px solid ${designColors.slate200}`,
          }}
        >
          <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
            {editMode ? (
              <>
                <Mui.Typography sx={{ ...companyUi.panelTitle, fontSize: 17 }}>
                  {company?.companyname || "N/A"}
                </Mui.Typography>
                <Mui.Typography sx={{ ...companyUi.panelMutedText, mt: 0.4 }}>
                  Editing this company's profile and access details.
                </Mui.Typography>
              </>
            ) : (
              // Generic panel title + helper subtitle -- same pattern as
              // UserDetailPanel's "User Details" / "Review user
              // information, access details, and account status." header.
              // The company's own name/avatar/status now live in the
              // summary card below instead of doubling as this title.
              <>
                <Mui.Typography sx={{ ...companyUi.panelTitle, mb: 0.35 }}>
                  Company Details
                </Mui.Typography>
                <Mui.Typography sx={companyUi.panelMutedText}>
                  Review company information, access details, and account
                  status.
                </Mui.Typography>
              </>
            )}
          </Mui.Box>
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            {/* Hidden rather than shown-and-blocked for a non-editor --
                same treatment as every other Administration create/edit
                action. There was no check at all here before. */}
            {!editMode && checkAccess ? (
              expanded ? (
                <PanelHeaderIconButton
                  expanded
                  title="Edit"
                  onClick={() => {
                    onEdit?.(company);
                    setEditMode(true);
                  }}
                >
                  <EditIcon />
                </PanelHeaderIconButton>
              ) : (
                <Mui.Tooltip title="Edit company">
                  <Mui.IconButton
                    aria-label="Edit company"
                    size="small"
                    onClick={() => {
                      onEdit?.(company);
                      setEditMode(true);
                    }}
                    sx={{
                      ...designShell.panelSoftIconButton,
                      color: designColors.slate500,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Mui.IconButton>
                </Mui.Tooltip>
              )
            ) : null}
            {expanded ? (
              <PanelHeaderIconButton
                expanded
                title="Collapse"
                onClick={onToggleExpand}
              >
                <CloseFullscreenIcon />
              </PanelHeaderIconButton>
            ) : (
              <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                <Mui.IconButton
                  onClick={onToggleExpand}
                  size="small"
                  sx={{ ...designShell.panelSoftIconButton }}
                >
                  <OpenFullViewIcon fontSize="small" />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}
            {expanded ? (
              <PanelHeaderIconButton
                expanded
                title="Close"
                onClick={handleCloseAction}
              >
                <CloseIcon />
              </PanelHeaderIconButton>
            ) : (
              <Mui.IconButton
                onClick={handleCloseAction}
                size="small"
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <CloseIcon />
              </Mui.IconButton>
            )}
          </Mui.Stack>
        </Mui.Stack>

        {!editMode ? (
          <Mui.Box sx={{ pb: 1.5 }}>
            <Mui.Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                border: `1px solid ${designColors.slate200}`,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${designColors.tealTint} 0%, ${designColors.white} 100%)`,
                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
              }}
            >
              <Mui.Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.5, sm: 2 }}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                {company?.company_logo_url ? (
                  <Mui.Box
                    sx={{
                      width: 116,
                      height: 56,
                      flexShrink: 0,
                      border: `1px solid ${designColors.slate200}`,
                      borderRadius: 1,
                      backgroundColor: designColors.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={company.company_logo_url}
                      alt={company?.companyname || "Company Logo"}
                      style={{
                        display: "block",
                        width: "auto",
                        height: "auto",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        objectPosition: "center",
                      }}
                    />
                  </Mui.Box>
                ) : (
                  <Mui.Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      border: `1px solid ${designColors.slate200}`,
                    }}
                    alt={company?.companyname?.charAt(0)?.toUpperCase()}
                  />
                )}
                <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
                  <Mui.Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ rowGap: 0.75 }}
                  >
                    <Mui.Typography
                      sx={{
                        fontSize: { xs: 17, sm: 18 },
                        fontWeight: 700,
                        color: designColors.slate900,
                        lineHeight: 1.2,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {company?.companyname || "N/A"}
                    </Mui.Typography>
                    {statusChip(isActive ? "Active" : "Inactive")}
                  </Mui.Stack>
                  <Mui.Stack
                    direction="row"
                    spacing={0.9}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mt: 1.1, minWidth: 0, rowGap: 0.75 }}
                  >
                    <HeaderMetaPill>
                      {company?.company_code || "N/A"}
                    </HeaderMetaPill>
                    <HeaderMetaPill
                      icon={
                        <BusinessIcon
                          sx={{ fontSize: 14, color: designColors.slate500 }}
                        />
                      }
                    >
                      {company?.isprimary ? "Primary" : "Secondary"}
                    </HeaderMetaPill>
                  </Mui.Stack>
                </Mui.Box>
              </Mui.Stack>
            </Mui.Box>
          </Mui.Box>
        ) : null}

        <Mui.Box
          sx={{ borderBottom: `1px solid ${designColors.slate200}` }}
        />
      </Mui.Box>

      <Mui.Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, pt: 2, pb: 2 }}>
        {editMode ? editFormContent : viewContent}
      </Mui.Box>

      {footerContent ? (
        <Mui.Box
          sx={{
            px: 3,
            py: 1.25,
            borderTop: "1px solid #E2E8F0",
            backgroundColor: designColors.white,
          }}
        >
          {footerContent}
        </Mui.Box>
      ) : null}
    </Mui.Box>
  );

  const removeLogoDialog = (
    <Mui.Dialog
      open={removeLogoDialogOpen}
      onClose={() => setRemoveLogoDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <Mui.DialogTitle sx={{ fontWeight: 700 }}>
        Remove company logo?
      </Mui.DialogTitle>
      <Mui.DialogContent>
        <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700 }}>
          The company logo will be removed from the company profile,
          application header and payslip after you click Update.
        </Mui.Typography>
      </Mui.DialogContent>
      <Mui.DialogActions sx={{ px: 3, pb: 2 }}>
        <SecondaryButton onClick={() => setRemoveLogoDialogOpen(false)}>
          Cancel
        </SecondaryButton>
        <Mui.Button
          variant="outlined"
          color="error"
          onClick={() => {
            setLogoFile(null);
            setLogoMeta(null);
            setLogoPreview("");
            setLogoMarkedForRemoval(true);
            setRemoveLogoDialogOpen(false);
          }}
          sx={{ textTransform: "none", borderRadius: designRadii.control }}
        >
          Remove Logo
        </Mui.Button>
      </Mui.DialogActions>
    </Mui.Dialog>
  );

  if (expanded) {
    return createPortal(
      <>
        <Mui.Box
          sx={{
            position: "fixed",
            top: APP_BAR_OFFSET,
            left: `${contentLeft}px`,
            right: 0,
            bottom: 0,
            zIndex: 10,
            overflow: "hidden",
            backgroundColor: designColors.pageBg,
            p: 2,
            boxSizing: "border-box",
          }}
        >
          <Mui.Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
            {panelContent}
          </Mui.Box>
        </Mui.Box>
        {removeLogoDialog}
      </>,
      document.body,
    );
  }

  return (
    <>
      <Mui.Drawer
        anchor="right"
        open={open}
        onClose={handleCloseAction}
        sx={panelDrawerSx()}
      >
        {panelContent}
      </Mui.Drawer>
      {removeLogoDialog}
    </>
  );
};
